import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, DragIndicator, Timer, Build as BuildIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RecipeV2Detail, RecipeStep, CreateUpdateStepRequest, ProductionMethod, Equipment } from '../types';
import useRecipeV2Api from '../hooks/useRecipeV2Api';

interface MethodTabProps {
  recipe: RecipeV2Detail;
  editable: boolean;
  onMetadataChange?: (partial: Partial<RecipeV2Detail>) => void;
}

interface StepCardProps {
  step: RecipeStep;
  index: number;
  editable: boolean;
  productionMethodName?: string;
  onEdit: (step: RecipeStep) => void;
  onRemove: (stepId: number) => void;
}

/**
 * Draggable step card, laid out in a responsive grid (see the `Box` grid container in
 * MethodTab below). Each card is a self-contained, vertically-stacked block of the
 * step's structured fields - title, description, method type/duration/temperature tags,
 * and equipment - styled after the Task Templates step builder. Plain display only;
 * editing happens via the Add/Edit dialog.
 */
const StepCard = ({ step, index, editable, productionMethodName, onEdit, onRemove }: StepCardProps) => {
  const { t } = useTranslation('recipeV2');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={() => editable && onEdit(step)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        cursor: editable ? 'pointer' : 'default',
        '&:hover': editable ? { borderColor: 'primary.main' } : undefined,
      }}
    >
      {/* Card header: drag handle + step number on the left, edit/delete on the right */}
      <Stack direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}>
        <Stack direction="row"
          alignItems="center"
          spacing={1.5}>
          {editable && (
            <Box
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', cursor: 'grab' }}
            >
              <DragIndicator fontSize="medium" />
            </Box>
          )}
          <Chip
            label={index + 1}
            size="small"
            color="primary"
            sx={{ minWidth: 32, height: 28, fontWeight: 700 }}
          />
        </Stack>
        {editable && (
          <Stack direction="row"
            spacing={0.5}
            onClick={(e) => e.stopPropagation()}>
            <IconButton size="small"
              onClick={() => onEdit(step)}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small"
              color="error"
              onClick={() => onRemove(step.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Typography variant="textMD"
        fontWeight={600}>
        {step.title || `${t('build.steps')} ${index + 1}`}
      </Typography>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mt: 1, flex: 1 }}>
        {step.instruction}
      </Typography>

      {(productionMethodName || step.durationMinutes != null || step.temperature) && (
        <Stack direction="row"
          spacing={1}
          sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap>
          {productionMethodName && (
            <Chip icon={<BuildIcon sx={{ fontSize: 14 }} />}
              label={productionMethodName}
              size="small"
              variant="outlined" />
          )}
          {step.durationMinutes != null && (
            <Chip icon={<Timer sx={{ fontSize: 14 }} />}
              label={`${step.durationMinutes} min`}
              size="small"
              variant="outlined" />
          )}
          {step.temperature && (
            <Chip label={`🌡️ ${step.temperature}`}
              size="small"
              variant="outlined" />
          )}
        </Stack>
      )}
      {step.equipment.length > 0 && (
        <Stack spacing={0.75}
          sx={{ mt: 2 }}>
          <Typography variant="textXS"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.5px' }}>
            {t('build.stepEquipment')}
          </Typography>
          <Stack direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap>
            {step.equipment.map((eq) => (
              <Chip key={eq.id}
                label={eq.name}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }} />
            ))}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

interface StepFormState {
  title: string;
  instruction: string;
  productionMethodId: number | '';
  durationMinutes: string;
  temperature: string;
  equipmentNames: string[];
}

const emptyStepForm: StepFormState = {
  title: '',
  instruction: '',
  productionMethodId: '',
  durationMinutes: '',
  temperature: '',
  equipmentNames: [],
};

const MethodTab = ({ recipe, editable, onMetadataChange }: MethodTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const [steps, setSteps] = useState<RecipeStep[]>(recipe.steps ?? []);
  const [productionMethods, setProductionMethods] = useState<ProductionMethod[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<RecipeStep | null>(null);
  const [stepForm, setStepForm] = useState<StepFormState>(emptyStepForm);

  useEffect(() => {
    api.getProductionMethods().then(setProductionMethods).catch(() => setProductionMethods([]));
  }, [api]);

  const productionMethodName = (id: number | null) => productionMethods.find((m) => m.id === id)?.name;

  // Equipment already used across this recipe's steps, offered as Autocomplete suggestions.
  const equipmentOptions = useMemo(() => {
    const seen = new Map<string, Equipment>();
    steps.forEach((step) => step.equipment.forEach((eq) => seen.set(eq.name.toLowerCase(), eq)));
    return Array.from(seen.values());
  }, [steps]);

  const resolveEquipment = (names: string[]): Equipment[] => {
    let nextId = Math.max(0, ...equipmentOptions.map((eq) => eq.id)) + 1;
    return names.map((name) => {
      const existing = equipmentOptions.find((eq) => eq.name.toLowerCase() === name.toLowerCase());
      if (existing) return existing;
      return { id: nextId++, name };
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex).map((s, i) => ({ ...s, stepNumber: i + 1 }));
      api.reorderSteps(recipe.id, reordered.map((s) => s.id)).catch(() => {});
      return reordered;
    });
  };

  const handleAddStep = async () => {
    const equipment = resolveEquipment(stepForm.equipmentNames);
    const newStep: CreateUpdateStepRequest = {
      stepNumber: steps.length + 1,
      title: stepForm.title,
      instruction: stepForm.instruction,
      productionMethodId: stepForm.productionMethodId === '' ? null : stepForm.productionMethodId,
      durationMinutes: stepForm.durationMinutes ? Number(stepForm.durationMinutes) : null,
      temperature: stepForm.temperature || null,
      equipmentIds: equipment.map((e) => e.id),
    };
    try {
      const saved = await api.addStep(recipe.id, newStep);
      setSteps((prev) => [...prev, { ...saved, equipment }]);
      setStepForm(emptyStepForm);
      setAddDialogOpen(false);
    } catch { /* noop */ }
  };

  const handleUpdateStep = async () => {
    if (!editingStep) return;
    const equipment = resolveEquipment(stepForm.equipmentNames);
    const updated: CreateUpdateStepRequest = {
      stepNumber: editingStep.stepNumber,
      title: stepForm.title,
      instruction: stepForm.instruction,
      productionMethodId: stepForm.productionMethodId === '' ? null : stepForm.productionMethodId,
      durationMinutes: stepForm.durationMinutes ? Number(stepForm.durationMinutes) : null,
      temperature: stepForm.temperature || null,
      equipmentIds: equipment.map((e) => e.id),
    };
    try {
      const saved = await api.updateStep(recipe.id, editingStep.id, updated);
      setSteps((prev) => prev.map((s) => s.id === editingStep.id ? { ...saved, equipment } : s));
      setEditingStep(null);
      setStepForm(emptyStepForm);
    } catch { /* noop */ }
  };

  const handleRemoveStep = async (stepId: number) => {
    try {
      await api.removeStep(recipe.id, stepId);
      setSteps((prev) => prev.filter((s) => s.id !== stepId).map((s, i) => ({ ...s, stepNumber: i + 1 })));
    } catch { /* noop */ }
  };

  const openEditStep = (step: RecipeStep) => {
    setEditingStep(step);
    setStepForm({
      title: step.title,
      instruction: step.instruction,
      productionMethodId: step.productionMethodId ?? '',
      durationMinutes: step.durationMinutes?.toString() ?? '',
      temperature: step.temperature ?? '',
      equipmentNames: step.equipment.map((e) => e.name),
    });
  };

  const handleProductionMethodChange = (value: number | '') => {
    const method = value === '' ? null : productionMethods.find((m) => m.id === value) ?? null;
    onMetadataChange?.({ productionMethodId: value === '' ? null : value, productionMethod: method });
  };

  return (
    <Stack spacing={2}>
      {/* Recipe method meta bar - fields styled the same as the create wizard (TextField + built-in label), editable */}
      <Stack direction="row"
        spacing={3}
        alignItems="flex-start"
        flexWrap="wrap"
        sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <TextField
          label={t('build.productionMethod')}
          select
          value={recipe.productionMethodId ?? ''}
          onChange={(e) => handleProductionMethodChange(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={!editable}
          sx={{ width: 200 }}
        >
          <MenuItem value="">{t('create.none')}</MenuItem>
          {productionMethods.map((pm) => (
            <MenuItem key={pm.id}
              value={pm.id}>{pm.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('build.effectiveFrom')}
          type="date"
          value={recipe.effectiveFrom}
          onChange={(e) => onMetadataChange?.({ effectiveFrom: e.target.value })}
          disabled={!editable}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label={t('build.effectiveTo')}
          type="date"
          value={recipe.effectiveTo ?? ''}
          onChange={(e) => onMetadataChange?.({ effectiveTo: e.target.value || null })}
          disabled={!editable}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <Box flex={1} />
        {editable && (
          <Button size="md"
            startIcon={<Add />}
            variant="primary"
            onClick={() => { setStepForm(emptyStepForm); setAddDialogOpen(true); }}>
            {t('build.addStep')}
          </Button>
        )}
      </Stack>

      {/* Step cards with DnD reordering, laid out as a responsive grid */}
      {steps.length > 0 ? (
        <DndContext sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}>
          <SortableContext items={steps.map((s) => s.id)}
            strategy={rectSortingStrategy}>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                  md: 'repeat(3, 1fr)',
                },
                alignItems: 'stretch',
              }}
            >
              {steps.map((step, index) => (
                <StepCard
                  key={step.id}
                  step={step}
                  index={index}
                  editable={editable}
                  productionMethodName={productionMethodName(step.productionMethodId)}
                  onEdit={openEditStep}
                  onRemove={handleRemoveStep}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      ) : (
        <Typography variant="textSM"
          color="text.secondary">{t('build.noSteps')}</Typography>
      )}

      {/* Add/Edit Step Dialog */}
      <Dialog open={addDialogOpen || editingStep !== null}
        onClose={() => { setAddDialogOpen(false); setEditingStep(null); }}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{editingStep ? t('build.editStep') : t('build.addStep')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('build.stepTitle')}
              value={stepForm.title}
              onChange={(e) => setStepForm((p) => ({ ...p, title: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('build.stepInstruction')}
              value={stepForm.instruction}
              onChange={(e) => setStepForm((p) => ({ ...p, instruction: e.target.value }))}
            />
            <TextField
              fullWidth
              select
              label={t('build.stepProductionMethod')}
              value={stepForm.productionMethodId}
              onChange={(e) => setStepForm((p) => ({ ...p, productionMethodId: e.target.value === '' ? '' : Number(e.target.value) }))}
            >
              <MenuItem value="">{t('create.none')}</MenuItem>
              {productionMethods.map((pm) => (
                <MenuItem key={pm.id}
                  value={pm.id}>{pm.name}</MenuItem>
              ))}
            </TextField>
            <Autocomplete
              multiple
              freeSolo
              options={equipmentOptions.map((eq) => eq.name)}
              value={stepForm.equipmentNames}
              onChange={(_, value) => setStepForm((p) => ({ ...p, equipmentNames: value }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <Chip label={option}
                    size="small"
                    {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params}
                  label={t('build.stepEquipment')}
                  placeholder={t('build.stepEquipmentPlaceholder')}
                />
              )}
            />
            <Stack direction="row"
              spacing={2}>
              <TextField
                type="number"
                label={t('build.stepDuration')}
                value={stepForm.durationMinutes}
                onChange={(e) => setStepForm((p) => ({ ...p, durationMinutes: e.target.value }))}
                sx={{ width: 200 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label={t('build.stepTemperature')}
                value={stepForm.temperature}
                onChange={(e) => setStepForm((p) => ({ ...p, temperature: e.target.value }))}
                placeholder={t('build.stepTemperaturePlaceholder')}
                sx={{ width: 200 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); setEditingStep(null); }}
            variant="secondary">{t('actions.cancel')}</Button>
          <Button
            onClick={editingStep ? handleUpdateStep : handleAddStep}
            variant="primary"
            disabled={!stepForm.instruction.trim()}>
            {editingStep ? t('actions.save') : t('build.addStep')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MethodTab;
